// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { formatLocalDate } from '@/services/localDate';

/** A personal access token as the BFF lists it: everything but the secret. */
export type ApiToken = {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
};

/** The create reply. `token` is the plaintext, shown once and never stored by the app. */
export type CreatedApiToken = ApiToken & { token: string };

export const API_TOKEN_NAME_MAX_LENGTH = 64;

/** The developer page on the marketing site, per locale. */
export const developerDocsUrl = (locale: string): string =>
  locale === 'sv' || locale === 'da' ? `https://www.goffy.app/${locale}/developers/` : 'https://www.goffy.app/developers/';

const asString = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null);

const asNullableString = (value: unknown): string | null | undefined => {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : undefined;
};

export const normalizeApiToken = (raw: unknown): ApiToken | null => {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = asString(record.id);
  const name = asString(record.name);
  const tokenPrefix = asString(record.tokenPrefix);
  const createdAt = asString(record.createdAt);
  const lastUsedAt = asNullableString(record.lastUsedAt);
  const expiresAt = asNullableString(record.expiresAt);
  if (!id || !name || !tokenPrefix || !createdAt || lastUsedAt === undefined || expiresAt === undefined) {
    return null;
  }
  return { id, name, tokenPrefix, createdAt, lastUsedAt, expiresAt };
};

export const normalizeApiTokenList = (raw: unknown): ApiToken[] =>
  Array.isArray(raw) ? raw.map(normalizeApiToken).filter((token): token is ApiToken => token !== null) : [];

export const normalizeCreatedApiToken = (raw: unknown): CreatedApiToken | null => {
  const token = normalizeApiToken(raw);
  const secret = token && raw && typeof raw === 'object' ? asString((raw as Record<string, unknown>).token) : null;
  return token && secret ? { ...token, token: secret } : null;
};

/**
 * The download name is decided here rather than read from the response: the server's
 * Content-Disposition is not readable cross-origin, and the share helper never looks at it.
 */
export const exportFileName = (date: Date = new Date()): string => `goffy-export-${formatLocalDate(date)}.json`;
